import LuminisServiceUtils from "./utils/luminis-service-utils.es6.js";
import SessionStorageUtils from "./utils/session-storage-utils.es6.js";
import MyChartUser from "./mychart-user.es6.js";
import DashboardEditAppointmentModal from "./dashboard-edit-appointment-modal.es6.js";
import ApiUtils from "./api-utils.es6.js";

export default class Dashboard {
  constructor() {
    this.visitType = '';
    this.visitTime = '';
    this.month = '';
    this.day = '';
    this.selectedOption = null;

    this.greetingTextList = document.querySelectorAll(
      '.greeting-box .greeting-text',
    );
    const officeVisitSelector = document.querySelector(
      '.visits .label-fields .field--name-field-visits-joining-text .field__item',
    );
    this.officeVisitJoiningText = officeVisitSelector
      ? officeVisitSelector.innerHTML.trim()
      : '';

    const appointmentJoiningTextSelector = document.querySelector(
      '.visits .label-fields .field--name-field-visit-appointment-joining- .field__item',
    );
    this.appointmentJoiningText = appointmentJoiningTextSelector
      ? appointmentJoiningTextSelector.innerHTML.trim()
      : '';

    const upcomingVisitPrefixTextSelector = document.querySelector(
      '.visits .label-fields .field--name-field-upcoming-visits-beginning- .field__item',
    );
    this.upcomingVisitPrefixText = upcomingVisitPrefixTextSelector
      ? upcomingVisitPrefixTextSelector.innerHTML.trim()
      : '';

    const upcomingVisitSuffixTextSelector = document.querySelector(
      '.visits .label-fields .field--name-field-view-upcoming-visits-end-t .field__item',
    );
    this.upcomingVisitSuffixText = upcomingVisitSuffixTextSelector
      ? upcomingVisitSuffixTextSelector.innerHTML.trim()
      : '';

    this.luminisServiceUtils = new LuminisServiceUtils();
  }

  async initPage() {
    this.myChartUser = await MyChartUser.getLoggedInUser();

    if (!this.myChartUser) Dashboard.hideLoggedInContent();

    this.setFirstName();
    await Dashboard.getCareTeam();
    const visitJson = await this.getVisits();
    await this.setMessageCount();
    await Dashboard.setReferralsCount();
    new DashboardEditAppointmentModal(visitJson, this.updateDashboardRecentVisitCallback.bind(this));
  }

  async updateDashboardRecentVisitCallback() {
    this.visitJson = await this.getVisits();
    document
      .querySelector('.visits .appointment-box')
      .classList.toggle('hidden');
    document.querySelector('.visits .loading-box').classList.toggle('hidden');
  }

  setFirstName() {
    if (!this.myChartUser) return;

    this.greetingTextList.forEach(greeting => {
      greeting.innerHTML = greeting.innerHTML.concat(
        ` ${this.myChartUser.firstName}`,
      );
    });
  }

  static async getProvidersAndLocationsOfVisits(visitList) {
    const providersAndLocations = new Map();
    const processedProviders = new Set();
    const processedLocations = new Set();

    // Use array method (map) to process visits
    visitList.forEach(visit => {
      if (visit.provider?.npi && !processedProviders.has(visit.provider.npi)) {
        processedProviders.add(visit.provider.npi);
      } else if (!processedLocations.has(visit.provider.id)) {
        processedLocations.add(visit.provider.id);
      }
    });

    // Fetch providers and locations
    if (processedProviders.size > 0) {
      const provider = await ApiUtils.searchProvidersByNpi(
        [...processedProviders]
      );
      Dashboard.addReturnedNPIResultsToList(provider, providersAndLocations);
    }

    if (processedLocations.size > 0) {
      const location = await Dashboard.searchLocationsById(
        [...processedLocations]
      );
      Dashboard.addReturnedNPIResultsToList(location, providersAndLocations);
    }

    return providersAndLocations;
  }

  static addReturnedNPIResultsToList(list, listToAddTo) {
    if (Array.isArray(list)) {
      // Iterate through each provider object in the array and extract their rows
      list.forEach(p => {
        if (p.rows) {
          p.rows.forEach(row => {
            if (row.field_provider_npi_number) {
              listToAddTo.set(row.field_provider_npi_number, row);
            } else {
              row.field_epic_office_id.split(',').forEach(id => listToAddTo.set(id, row));
            }
          });
        }
      });
    } else if (list.rows) {
      list.rows.forEach(row => {
        if (row.field_provider_npi_number) {
          listToAddTo.set(row.field_provider_npi_number, row);
        } else {
          row.field_epic_office_id.split(',').forEach(id => listToAddTo.set(id, row));
        }
      });
    }
  }

  static async searchLocationsById(ids) {
    let idsString = '';
    if (typeof ids === 'string') {
      idsString = ids;
    } else {
      idsString = ApiUtils.createUrlFriendlyStringParamsFromArray(
        ids,
      );
    }

    return fetch(
      `/${
        window.location.pathname.split('/')[1]
      }/search-location/rest?search=&epic_id=${idsString}&page=0`,
    )
    .then(res => res.json())
    .then(
      results => results,
      error => {
        // eslint-disable-next-line no-console
        console.error(error);
      },
    );
  }

  async getVisits() {
    const visitList = await this.luminisServiceUtils.getPatientFutureAppointments();
    const futureAndPastDatesMap = Dashboard.getUpcomingAndPastDates(
      visitList,
    );

    if (futureAndPastDatesMap.futureDates.length > 0) {
      const firstAppointment = futureAndPastDatesMap.futureDates[0];
      const futureProvidersAndLocationsList = await Dashboard.getProvidersAndLocationsOfVisits(
        futureAndPastDatesMap.futureDates,
      );

      if (
        futureProvidersAndLocationsList &&
        futureProvidersAndLocationsList.size > 0
      ) {
        let firstVisit;
        if (firstAppointment.provider.npi) {
          firstVisit = futureProvidersAndLocationsList.get(firstAppointment.provider.npi);
        } else {
          firstVisit = futureProvidersAndLocationsList.get(firstAppointment.provider.id);
        }
        this.setAppointmentInformation(
          firstVisit,
          firstAppointment,
          futureAndPastDatesMap.futureDates.length,
        );
        return firstAppointment;
      }
    }

    // show empty card
    document
      .querySelector('.visits .no-appointments')
      .classList.toggle('hidden');
    document.querySelector('.visits .loading-box').classList.toggle('hidden');
    return visitList;
  }

  setAppointmentInformation(providerOrLocation, visit, futureDateCount) {
    // build office visit string
    const visitTypeStr = Dashboard.capitalizer(visit.visitType.name);
    let providerOrLocationName = '';

    if (providerOrLocation) {
      if (providerOrLocation.field_epic_office_id) {
        providerOrLocationName = providerOrLocation.field_location_name_1;
      }
      else {
        providerOrLocationName = providerOrLocation.field_hero_banner_text_1;
      }
    } else {
      // first appointment provider info is missing, probably unpublished. Use placeholders.
      // TODO LH-2804 - Fix this per decision
      providerOrLocationName = 'Unknown Provider';
    }

    this.visitType = `${visitTypeStr} ${this.officeVisitJoiningText} ${providerOrLocationName}`;
    document.querySelector(
      '.visits .appointment .visit-type',
    ).innerHTML = this.visitType;

    const dateMap = Dashboard.getDateTimeSlotsParsed(
      visit.slot,
      this.appointmentJoiningText,
    );

    this.month = dateMap.month;
    document.querySelector(
      '.visits .appointment .date .month',
    ).innerHTML = this.month;

    this.day = dateMap.day;
    document.querySelector(
      '.visits .appointment .date .day',
    ).innerHTML = this.day;

    this.visitTime = dateMap.longDate;
    document.querySelector(
      '.visits .appointment .visit-time',
    ).innerHTML = this.visitTime;

    const viewDetailsLink = document.querySelector(
      '.field--name-field-view-details-link .external-link a',
    );

    if (viewDetailsLink && visit.visitUrl) {
      viewDetailsLink.setAttribute('href', visit.visitUrl);
      viewDetailsLink.removeAttribute('hidden');
    } else {
      viewDetailsLink.setAttribute('href', visit.visitUrl);
      viewDetailsLink.setAttribute('hidden', true);
    }

    viewDetailsLink.setAttribute('target', '_blank');

    this.providerPhone = visit.department.phone;
    const phoneElement = document.querySelector(
      '.visits .appointment .provider-phone .phone-link'
    );
    if (this.providerPhone) {
      phoneElement.href = "tel:".concat(this.providerPhone);
      phoneElement.innerHTML = this.providerPhone;
    } else {
      phoneElement.classList.add('hidden');
    }

    if (visit.questionnaires && visit.questionnaires.length > 0) {
      document
        .querySelector('.visits .questionnaires-box')
        .classList.toggle('hidden');
    }

    // build upcoming visit text
    document.querySelector(
      '.visits .upcoming-visits-box a',
    ).innerHTML = `${this.upcomingVisitPrefixText} ${futureDateCount} ${this.upcomingVisitSuffixText}`;

    document.querySelector('.visits .loading-box').classList.toggle('hidden');
    document
      .querySelector('.visits .appointment-box')
      .classList.toggle('hidden');
  }

  static getDateTimeSlotsParsed(timeslot, joining) {
    const date = new Date(timeslot);
    const localString = date
      .toLocaleTimeString('en-US', {
        month: 'short',
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
      })
      .split(' ');
    const month = localString[0];
    const day = date.getDate();
    const dayOfWeek = localString[1].replaceAll(',', '');
    const time = `${localString[2]} ${localString[3]}`;
    const year = date.getFullYear();
    const dateTimeString = `${dayOfWeek}, ${month} ${day} ${joining} ${time}`;
    const dateString = `${dayOfWeek}, ${month} ${day}, ${year}`;

    return {
      longDate: dateTimeString,
      day,
      month,
      time,
      dateString,
    };
  }

  static capitalizer(word) {
    const splitStr = word.toLowerCase().split(' ');
    for (let i = 0; i < splitStr.length; i++) {
      splitStr[i] =
        splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
    }

    return splitStr.join(' ');
  }

  static async getCareTeam() {
    let providerNPI;

    const luminisServiceUtils = new LuminisServiceUtils();
    const pcpJson = await luminisServiceUtils.getPatientPCP();

    if (pcpJson) {
      providerNPI = pcpJson;
      if (providerNPI.length > 0) {
        const provObj = (
          await ApiUtils.searchProvidersByNpi(providerNPI)
        ).rows;

        if (provObj.length > 0) {
          await Dashboard.buildScheduleOnlineButton(provObj[0]);
          Dashboard.setProviderInformation(provObj[0], true);
          return;
        }
      }
    }

    // call patient and care team instead to get a list of NPIs
    const careTeamJson = await luminisServiceUtils.fetchLoggedInCareTeam();

    if (careTeamJson && careTeamJson.length > 0) {
      const provObjList = (
        await ApiUtils.searchProvidersByNpi(
          careTeamJson,
        )
      ).rows;

      if (provObjList.length > 0) {
        await Dashboard.buildScheduleOnlineButton(provObjList[0]);
        Dashboard.setProviderInformation(provObjList[0], false);
        return;
      }
    }

    // hide provider card and show empty card instead and last seen label if we get to this point
    document.querySelector('.care-team .pcp-label').classList.toggle('hidden');
    document
      .querySelector('.care-team .last-seen-label')
      .classList.toggle('hidden');
    document
      .querySelector('.care-team .providers-not-found')
      .classList.toggle('hidden');
    document
      .querySelector('.care-team .loading-box')
      .classList.toggle('hidden');
  }

  static async buildScheduleOnlineButton(providerObj) {
    // get mnemonic for the current providers npi
    const npi = providerObj.field_provider_npi_number;
    const luminisServiceUtils = new LuminisServiceUtils();
    const exclusionsList = await LuminisServiceUtils.getExclusionMnemonics();
    const mnemonicJson = await luminisServiceUtils.getProviderOnlineSchedulingAvailabilityInfo(
      npi,
      exclusionsList,
    );
    const linkToChange = document.querySelector('.schedule-or-call-link');

    if (mnemonicJson && mnemonicJson.visitReasonMnemonicAvailability.length === 0) {
      // show call to schedule button here
      const linkText = document.querySelector(
        '.field--name-field-call-to-schedule-text .field__item',
      );
      linkToChange.innerHTML = linkText.textContent;
      const primaryLocationJson = await ApiUtils.searchLocationByNid(
        providerObj.provider_primary_location_nid,
      );

      if (primaryLocationJson && primaryLocationJson.rows.length > 0) {
        const phoneLink = primaryLocationJson.rows[0].field_phone_1;
        const parser = new DOMParser();
        const linkElement = parser.parseFromString(phoneLink, 'text/html');
        linkToChange.href = linkElement.body.firstElementChild.href;
      } else {
        // no phone number hide button.
        document.querySelector('.schedule-online-box').classList.add('hidden');
      }
    } else {
      const linkText = document.querySelector(
        '.field--name-field-schedule-online-text .field__item',
      );
      linkToChange.innerHTML = linkText.textContent;
      const linkToChangeHref = document.querySelector(
        '.field--name-field-schedule-with-provider-lin a',
      ).href;
      linkToChange.href = `${linkToChangeHref}?npi=${npi}`;

      linkToChange.onclick = event => {
        event.preventDefault();
        SessionStorageUtils.clearAppointmentSpecificSessionStorage();
        window.location = event.target.href;
      };
    }
    linkToChange.classList.remove('hidden');
  }

  static setProviderInformation(provider, isPcp) {
    // show pcp or care team label
    if (!isPcp) {
      document
        .querySelector('.care-team .pcp-label')
        .classList.toggle('hidden');
      document
        .querySelector('.care-team .last-seen-label')
        .classList.toggle('hidden');
    }

    // set photo
    if (provider.hero_url) {
      const uri = provider.hero_url;
      const image = document.querySelector(
        '.care-team .provider-photo .provider-info__image',
      );
      image.setAttribute('src', uri);
    } else {
      document
        .querySelector('.care-team .provider-photo-not-found')
        .classList.toggle('hidden');
      document
        .querySelector('.care-team .provider-photo')
        .classList.toggle('hidden');
    }

    // set name
    const name = document.querySelector('.care-team .provider-name');
    name.innerHTML = provider.title_1;

    // set specialties - limit 2 like find care does
    const specialties = document.querySelector(
      '.care-team .provider-specialties',
    );

    specialties.innerHTML = ApiUtils.getProviderSpecialtiesArray(
      provider,
    );

    // set link href
    const link = document.querySelector('.care-team .view-provider-link');
    const providerLink = provider.field_hero_banner_text;
    const parser = new DOMParser();
    const virtualDoc = parser.parseFromString(providerLink, 'text/html');
    const aTag = virtualDoc.getElementsByTagName('a')[0];
    link.setAttribute('href', aTag.href);

    // hide loading box and show real box:
    document
      .querySelector('.care-team .loading-box')
      .classList.toggle('hidden');
    document
      .querySelector('.care-team .provider-box')
      .classList.toggle('hidden');
  }

  async setMessageCount() {
    const messageCount = await this.getMessages();
    // update session storage for the block header
    sessionStorage.setItem('mychartMessageCount', messageCount);

    const messagesLink = document.querySelector(
      '.messages-box .field--name-field-messages-link a',
    );
    messagesLink.innerHTML = `${messageCount} ${messagesLink.innerHTML}`;
    document.querySelector('.messages-box').classList.toggle('hidden');
    document.querySelector('.messages-loading-box').classList.toggle('hidden');
  }

  static async setReferralsCount() {
    const luminisServiceUtils = new LuminisServiceUtils();
    const referralsJson = await luminisServiceUtils.getPatientSchedulingTickets();

    if (referralsJson && referralsJson.length > 0) {
      const referralCount = referralsJson.length;

      const referralsLink = document.querySelector(
        '.special-notice-box .field--name-field-special-notice-link a',
      );
      referralsLink.innerHTML = `${referralCount} ${referralsLink.innerHTML}`;
      document.querySelector('.special-notice-box').classList.toggle('hidden');
    }
  }

  async getMessages(lastMessageId) {
    const messages = await this.luminisServiceUtils.getPatientConversations();
    let convoCount = 0;

    if (messages) {
      const conversations = messages.conversations;
      conversations.forEach(conversation => {
        if (conversation.hasUnreadMessages) {
          conversation.messages.forEach(message => {
            if (!message.read) {
              convoCount += 1;
            }
          });
        }
      });

      lastMessageId = null;
      if (messages.hasMore) {
        lastMessageId = messages.lastMessageId;
      }

      // If another page exists, loop over this request again
      if (lastMessageId) {
        const count = await this.getMessages(lastMessageId);
        convoCount += count;
      }
    } else {
      convoCount = 0;
    }

    return convoCount;
  }

  static getUpcomingAndPastDates(visitsJson) {
    const futureDates = [];
    const pastDates = [];

    if (visitsJson) {
      visitsJson.forEach(visit => {
        const date = new Date(visit.slot);
        const now = new Date();

        if (date > now) {
          futureDates.push(visit);
        } else {
          pastDates.push(visit);
        }
      });
    }

    return {
      futureDates,
      pastDates,
    };
  }

  static hideLoggedInContent() {
    document
      .querySelector('.dashboard-not-logged-in')
      .classList.toggle('hidden');
    document.querySelector('.logged-in-content').classList.toggle('hidden');
  }
}

(() => {})(Drupal);
